import { useLazyQuery } from '@apollo/client'
import { isEmpty, uniqBy } from 'lodash'
import { ReactElement, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { Autocomplete, useDebouncedQuery } from 'components/v2/Autocomplete'
import { IAutocompleteProps } from 'components/v2/Autocomplete/types'
import { GET_ROUTES_SUGGESTIONS } from 'features/Routes/queries-legacy'
import { GTRouteSuggestion, GTRouteSuggestionsResponse } from 'gql/types.generated'

const messages = defineMessages({
  error: {
    id: 'routes.autocomplete.error',
    defaultMessage: 'There was an issue while loading your routes'
  },
  empty: {
    id: 'routes.autocomplete.empty',
    defaultMessage: 'No routes found'
  }
})

interface IRouteAutocompleteProps extends Omit<IAutocompleteProps, 'onChange' | 'items' | 'getItemLabel'> {
  onChange: (routes: GTRouteSuggestion[]) => void
  uniqueName?: boolean
  verticals?: string[]
  businessClientIds?: string[]
  businessSiteIds?: string[]
}

export const RouteAutocomplete = ({
  onChange,
  uniqueName = false,
  verticals,
  businessClientIds,
  businessSiteIds,
  ...props
}: IRouteAutocompleteProps): ReactElement => {
  const intl = useIntl()
  const [pattern, setPattern] = useState('')
  const [getBusinessCustomers, { data, loading, error }] = useLazyQuery<{
    routeSuggestions: GTRouteSuggestionsResponse
  }>(GET_ROUTES_SUGGESTIONS)
  const { isDebouncing, shouldShowItems } = useDebouncedQuery({
    // @ts-expect-error FIXME
    query: getBusinessCustomers,
    pattern,
    extraInputs: {
      verticals: !isEmpty(verticals) ? verticals : undefined,
      businessClientIDs: !isEmpty(businessClientIds) ? businessClientIds : undefined,
      businessSiteIDs: !isEmpty(businessSiteIds) ? businessSiteIds : undefined
    }
  })

  const handleChange = (route: GTRouteSuggestion, { inputValue = '' } = {}): void => {
    setPattern(inputValue)
    if (!isEmpty(route)) {
      let routes: GTRouteSuggestion[] = []
      if (uniqueName) {
        routes = data?.routeSuggestions.items.filter((item) => item.name === route.name) || []
      } else {
        routes = [route]
      }
      onChange(routes)
    } else {
      onChange([])
    }
  }

  const routes = data?.routeSuggestions?.items || []
  const shownItems = uniqueName ? uniqBy(routes, 'name') : routes

  return (
    <Autocomplete
      {...props}
      items={shownItems}
      getItemLabel={(route: GTRouteSuggestion): string => route.name}
      loading={loading || isDebouncing}
      error={error}
      errorMessage={intl.formatMessage(messages.error)}
      emptyMessage={intl.formatMessage(messages.empty)}
      onChange={handleChange}
      shouldShowItems={shouldShowItems}
    />
  )
}
